import Axios from "axios";
import { API_SERVER } from "../config/constant";

// Constantes para control de renovación de token
const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutos antes de expirar, renovar
const TOKEN_MIN_INTERVAL = 5000; // 5 segundos entre renovaciones para evitar cascadas
let lastTokenRefresh = 0;
let isRefreshing = false;
let failedQueue = [];

// Función para procesar cola de solicitudes pendientes cuando hay un token fresco
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Decodifica JWT para verificar expiración
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn("Error parseando JWT:", error);
    return null;
  }
};

// Verifica si el token está próximo a expirar
const isTokenExpiringSoon = (token) => {
  if (!token) return true;

  try {
    const payload = parseJwt(token);
    if (!payload || !payload.exp) return true;

    const expiryTime = payload.exp * 1000; // Convertir a milisegundos
    const currentTime = Date.now();

    return expiryTime - currentTime < TOKEN_EXPIRY_BUFFER;
  } catch (error) {
    console.error("Error verificando expiración del token:", error);
    return true;
  }
};

// Obtener token de Clerk con manejo mejorado de caché
const getClerkToken = async (forceRefresh = false) => {
  if (!window.Clerk?.session) return null;

  try {
    // Verificación de intervalo mínimo entre renovaciones
    const now = Date.now();
    const sinceLastRefresh = now - lastTokenRefresh;

    // Si hace poco se renovó y no es forzado, usar el cache
    if (!forceRefresh && sinceLastRefresh < TOKEN_MIN_INTERVAL) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const { token } = JSON.parse(storedUser);
        // Verificar que el token no esté próximo a expirar
        if (token && !isTokenExpiringSoon(token)) {
          return token;
        }
      }
    }

    // Obtener token nuevo desde Clerk
    const token = await window.Clerk.session.getToken({
      audience: process.env.REACT_APP_CLERK_JWT_AUDIENCE,
      sessionId: window.Clerk.session.id,
    });

    if (token) {
      lastTokenRefresh = now;

      // Actualizar el token en localStorage
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.token = token;
        localStorage.setItem("user", JSON.stringify(user));
      }

      return token;
    }

    return null;
  } catch (error) {
    console.error("Error obteniendo token de Clerk:", error);
    return null;
  }
};

// Crear instancia de axios
const axios = Axios.create({
  baseURL: API_SERVER,
  headers: { "Content-Type": "application/json" },
  timeout: 30000, // 30 segundos para evitar solicitudes infinitas
  withCredentials: true,
});

// Interceptor para añadir el token a las peticiones
axios.interceptors.request.use(
  async (config) => {
    // Si la petición indica que debe saltar autenticación
    if (config.skipAuth) {
      return config;
    }

    try {
      // Obtener usuario del localStorage primero como caché rápido
      const storedUser = localStorage.getItem("user");
      let token = null;

      if (storedUser) {
        const user = JSON.parse(storedUser);
        token = user.token;

        // Verificar expiración próxima del token y renovar silenciosamente
        if (isTokenExpiringSoon(token) && window.Clerk?.session) {
          // Intenta renovar, pero no bloquea la petición actual
          getClerkToken(true)
            .then((newToken) => {
              if (newToken && token !== newToken) {
                console.log("Token renovado silenciosamente");
              }
            })
            .catch((err) => {
              console.error("Error en renovación silenciosa:", err);
            });
        }
      }

      // Si no hay token en localStorage o está a punto de expirar (para peticiones críticas),
      // obtener uno nuevo de Clerk
      if (!token && window.Clerk?.session) {
        token = await getClerkToken();
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    } catch (error) {
      console.error("Error en interceptor de request:", error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar respuestas y errores de token
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Detectar errores de token expirado/inválido
    const isTokenError =
      error.response?.status === 401 &&
      (error.response?.data?.detail?.includes("Signature has expired") ||
        error.response?.data?.detail?.includes("Token inválido") ||
        error.response?.data?.detail?.includes("Token expirado") ||
        error.response?.data?.detail?.includes("Invalid token") ||
        error.response?.data?.detail?.includes("Expired signature") ||
        error.response?.data?.code === "token_not_valid");

    // Si es error de token y no tiene ya un retry
    if (isTokenError && !originalRequest._retry && !originalRequest.skipAuth) {
      originalRequest._retry = true;

      // Si ya hay un proceso de renovación en curso, encolar esta petición
      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        } catch (queueError) {
          // Si falla la cola, manejar elegantemente
          const event = new CustomEvent("auth:sessionExpired", { detail: { error: queueError } });
          window.dispatchEvent(event);
          return Promise.reject(queueError);
        }
      }

      isRefreshing = true;

      try {
        // Intentar renovar el token
        const token = await getClerkToken(true);
        if (!token) {
          throw new Error("No se pudo obtener un nuevo token");
        }

        originalRequest.headers.Authorization = `Bearer ${token}`;
        processQueue(null, token);

        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        // Notificar al sistema de autenticación del problema
        const event = new CustomEvent("auth:sessionExpired", { detail: { error: refreshError } });
        window.dispatchEvent(event);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Método para renovación manual del token
axios.refreshToken = async () => {
  try {
    const token = await getClerkToken(true);
    if (token) {
      return token;
    }
    return null;
  } catch (error) {
    console.error("Error en renovación manual de token:", error);
    return null;
  }
};

// Método para verificar si el token está próximo a expirar
axios.isTokenExpiringSoon = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  return !user?.token || isTokenExpiringSoon(user.token);
};

export default axios;
